import React, { useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { Camera as CameraIcon, Mic } from "lucide-react";
import { Button } from "@/components/ui/button";
import { IMAGE_PATH } from "../../api/apiConfig";
import { showErrorToast } from "../../js/utils/errors";
import useCameraStream from "../../hooks/useCameraStream";
import useBgmDucking from "../../hooks/useBgmDucking";
import useVideoRecording from "../../hooks/useVideoRecording";
import useAudioRecording from "../../hooks/useAudioRecording";
import useFloatingSuggestions from "../../hooks/useFloatingSuggestions";
import useImageCrop from "../../hooks/useImageCrop";
import useCameraLifecycle from "../../hooks/useCameraLifecycle";
import CameraStream from "./CameraStream";
import PhotoCaptureMode from "./PhotoCaptureMode";
import VideoRecordingMode from "./VideoRecordingMode";
import ImageCropTool from "./ImageCropTool";
import FloatingSuggestions from "./FloatingSuggestions";
import Waveform from "./Waveform";
import CameraHeader from "./CameraHeader";
import CameraControls from "./CameraControls";
import MediaPreviewPanel from "./MediaPreviewPanel";

/** Load a blob: URL as a JPEG File. */
function fetchBlobUrlAsJpegFile(blobUrl, filenamePrefix) {
	return fetch(blobUrl)
		.then((res) => {
			if (!res.ok) {throw new Error(`Failed to read image (${res.status})`);}
			return res.blob();
		})
		.then((blob) => {
			if (!blob || blob.size === 0) {throw new Error("Empty image data");}
			return new File([blob], `${filenamePrefix}-${Date.now()}.jpg`, {
				type: "image/jpeg",
			});
		});
}

/**
 * Custom Camera Component
 * Provides full control over camera UI and functionality
 */
const Camera = ({
	onCapture,
	onClose,
	mode: initialMode = "photo",
	showPreview = true,
	autoTriggerUpload = false,
	postType = null,
	isContest = false,
	delayInitialization = false,
	hideBottomBar = false,
	skipCrop = false,
	confirmButtonLabel = null,
}) => {
	// ── Refs ──────────────────────────────────────────────────────────────
	const canvasRef = useRef(null);
	const fileInputRef = useRef(null);
	const videoInputRef = useRef(null);
	const audioInputRef = useRef(null);
	const cameraContainerRef = useRef(null);
	const handleTrackEndedRef = useRef(null);

	// ── Core state ───────────────────────────────────────────────────────
	const [capturedImage, setCapturedImage] = useState(null);
	const [isUploadedMedia, setIsUploadedMedia] = useState(false);
	const [facingMode, setFacingMode] = useState(() => {
		const isMobile =
			/iPhone|iPad|iPod|Android/i.test(navigator.userAgent) ||
			window.innerWidth < 768;
		if (isMobile) {
			const saved = localStorage.getItem("cameraFacingMode");
			return saved === "user" ? "user" : "environment";
		}
		return "user";
	});
	const [mode, setMode] = useState(initialMode);
	const [hasMultipleCameras, setHasMultipleCameras] = useState(false);
	const [isMuted, setIsMuted] = useState(false);
	const [recordingDuration, setRecordingDuration] = useState(0);

	// ── Camera stream hook ───────────────────────────────────────────────
	const {
		videoRef,
		streamRef,
		stream,
		hasPermission,
		isInitializing,
		error,
		setError,
		setStream,
		setHasPermission,
		setIsInitializing,
		startCamera,
		stopCamera,
	} = useCameraStream({
		mode,
		facingMode,
		delayInitialization,
		autoTriggerUpload,
		onError: (msg) => setError(msg),
		onTrackEnded: (track) => {
			if (handleTrackEndedRef.current) {
				handleTrackEndedRef.current(track);
			}
		},
	});

	// ── BGM ducking ──────────────────────────────────────────────────────
	const { duckBgm, restoreBgm, duckBgmForMode } = useBgmDucking();

	const handleClose = useCallback(() => {
		stopCamera();
		onClose();
	}, [stopCamera, onClose]);

	// ── Video recording ──────────────────────────────────────────────────
	const {
		isRecording,
		capturedVideo,
		isUploadedVideo,
		startRecording,
		stopRecording,
		clearCapturedVideo,
		setUploadedVideo,
	} = useVideoRecording({
		streamRef,
		videoRef,
		facingMode,
		isMuted,
		showPreview,
		onCapture,
		onClose: handleClose,
		setStream,
		setError,
		duckBgm,
		restoreBgm,
	});

	// ── Audio recording ──────────────────────────────────────────────────
	const {
		isAudioRecording,
		audioLevels,
		capturedAudio,
		isUploadedAudio,
		startAudioRecording,
		stopAudioRecording,
		clearCapturedAudio,
		setUploadedAudio,
		cleanupAudio,
	} = useAudioRecording({
		streamRef,
		setStream,
		showPreview,
		onCapture,
		onClose: handleClose,
		duckBgm,
		restoreBgm,
		setError,
		setHasPermission,
	});

	// ── Image crop ───────────────────────────────────────────────────────
	const {
		showCrop,
		setShowCrop,
		imageToCrop,
		setImageToCrop,
		crop,
		imgRef,
		onCropChange,
		onCropComplete,
		onImageLoaded,
		applyCrop,
		cancelCrop,
		resetCrop,
	} = useImageCrop({
		showPreview,
		onCapture,
		onClose: handleClose,
		setError,
		setCapturedImage,
		fetchBlobAsJpeg: fetchBlobUrlAsJpegFile,
	});

	// ── Floating suggestions ─────────────────────────────────────────────
	const isCaptureDone = !!(capturedImage || capturedVideo || capturedAudio);
	const {
		suggestions: floatingSuggestions,
		specs: floatingSpecs,
		hasUserInteracted,
		handleUserInteraction,
	} = useFloatingSuggestions({
		postType,
		isContest,
		isCaptureDone,
		showCrop,
		autoTriggerUpload,
	});

	// ── Camera lifecycle (all the useEffect blocks) ──────────────────────
	useCameraLifecycle({
		cameraStream: {
			videoRef,
			streamRef,
			stream,
			hasPermission,
			isInitializing,
			setError,
			setStream,
			setHasPermission,
			setIsInitializing,
			startCamera,
			stopCamera,
		},
		recording: {
			isRecording,
			isAudioRecording,
			stopRecording,
			stopAudioRecording,
		},
		mode,
		facingMode,
		delayInitialization,
		autoTriggerUpload,
		cleanupAudio,
		cameraContainerRef,
		handleTrackEndedRef,
		setHasMultipleCameras,
		setRecordingDuration,
		duckBgmForMode,
	});

	// ── Helpers ──────────────────────────────────────────────────────────

	const switchCamera = () => {
		setFacingMode((prev) => {
			const next = prev === "user" ? "environment" : "user";
			localStorage.setItem("cameraFacingMode", next);
			return next;
		});
	};

	const formatDuration = (seconds) => {
		if (
			typeof seconds !== "number" ||
			isNaN(seconds) ||
			!isFinite(seconds) ||
			seconds < 0
		) {
			return "00:00";
		}
		const mins = Math.floor(seconds / 60);
		const secs = Math.floor(seconds % 60);
		return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
	};

	const handleUndo = () => {
		if (capturedImage) {URL.revokeObjectURL(capturedImage);}
		setCapturedImage(null);
		setShowCrop(true);
	};

	// ── Confirm / Retake / File select ───────────────────────────────────

	const handleConfirm = async () => {
		try {
			if (capturedImage) {
				const file = await fetchBlobUrlAsJpegFile(capturedImage, "photo");
				onCapture(file);
				handleClose();
			} else if (capturedVideo) {
				const res = await fetch(capturedVideo);
				if (!res.ok) {throw new Error(`Failed to read video (${res.status})`);}
				const blob = await res.blob();
				if (!blob || blob.size === 0) {throw new Error("Empty video data");}
				onCapture(
					new File([blob], `video-${Date.now()}.webm`, { type: "video/webm" })
				);
				handleClose();
			} else if (capturedAudio) {
				const res = await fetch(capturedAudio);
				if (!res.ok) {throw new Error(`Failed to read audio (${res.status})`);}
				const blob = await res.blob();
				if (!blob || blob.size === 0) {throw new Error("Empty audio data");}
				onCapture(
					new File([blob], `audio-${Date.now()}.webm`, { type: "audio/webm" })
				);
				handleClose();
			}
		} catch (err) {
			console.error(err);
			showErrorToast(
				"Could not save media",
				err?.message || "Please try again."
			);
		}
	};

	const handleRetake = async () => {
		resetCrop();
		if (capturedImage) {URL.revokeObjectURL(capturedImage);}
		setCapturedImage(null);
		clearCapturedVideo();
		clearCapturedAudio();
		if (isAudioRecording) {stopAudioRecording();}
		cleanupAudio();
		setRecordingDuration(0);
		setError(null);
		if (videoRef.current) {videoRef.current.srcObject = null;}

		if (isUploadedMedia || isUploadedVideo || isUploadedAudio) {
			if (fileInputRef.current) {fileInputRef.current.value = "";}
			if (videoInputRef.current) {videoInputRef.current.value = "";}
			if (audioInputRef.current) {audioInputRef.current.value = "";}
			setIsUploadedMedia(false);
			setTimeout(() => {
				if (mode === "photo") {fileInputRef.current?.click();}
				else if (mode === "video") {videoInputRef.current?.click();}
				else if (mode === "audio") {audioInputRef.current?.click();}
			}, 100);
		} else if (mode !== "audio") {
			await startCamera();
		}
	};

	const handleFileSelect = (e, type) => {
		const file = e.target.files[0];
		if (!file) {return;}
		setError(null);

		const validTypes = {
			image: ["image/jpeg", "image/png", "image/gif", "image/webp"],
			video: [
				"video/mp4",
				"video/webm",
				"video/ogg",
				"video/quicktime",
				"video/x-m4v",
			],
			audio: [
				"audio/mpeg",
				"audio/wav",
				"audio/ogg",
				"audio/webm",
				"audio/mp4",
			],
		};
		if (!validTypes[type]?.includes(file.type)) {
			setError(`Invalid file type. Please select a valid ${type} file.`);
			return;
		}
		const maxSize = type === "video" ? 100 * 1024 * 1024 : 50 * 1024 * 1024;
		if (file.size > maxSize) {
			setError(
				`File too large. Maximum size is ${type === "video" ? "100MB" : "50MB"}.`
			);
			return;
		}

		const previewUrl = URL.createObjectURL(file);
		if (type === "image") {
			setImageToCrop(previewUrl);
			setShowCrop(true);
			setIsUploadedMedia(true);
		} else if (type === "video") {
			setUploadedVideo(previewUrl);
			setIsUploadedMedia(true);
			if (streamRef.current) {
				streamRef.current.getTracks().forEach((track) => track.stop());
				streamRef.current = null;
				setStream(null);
			}
		} else if (type === "audio") {
			setUploadedAudio(previewUrl);
			setIsUploadedMedia(true);
		}
		if (!showPreview) {
			onCapture(file);
			handleClose();
		}
	};

	const handlePhotoCapture = (imageUrl) => {
		stopCamera();
		if (skipCrop) {
			fetchBlobUrlAsJpegFile(imageUrl, "photo")
				.then((file) => {
					URL.revokeObjectURL(imageUrl);
					onCapture(file);
					handleClose();
				})
				.catch((err) => {
					URL.revokeObjectURL(imageUrl);
					showErrorToast(
						"Could not save photo",
						err?.message || "Please try again."
					);
				});
		} else {
			setCapturedImage(imageUrl);
			setIsUploadedMedia(false);
			if (!showPreview) {
				fetchBlobUrlAsJpegFile(imageUrl, "photo")
					.then((file) => {
						URL.revokeObjectURL(imageUrl);
						onCapture(file);
						handleClose();
					})
					.catch((err) => {
						URL.revokeObjectURL(imageUrl);
						showErrorToast(
							"Could not save photo",
							err?.message || "Please try again."
						);
					});
			}
		}
	};

	// ── Render ───────────────────────────────────────────────────────────
	const hasCapturedMedia = !!(capturedImage || capturedVideo || capturedAudio);

	const renderMainContent = () => {
		if (isInitializing && !error) {
			return (
				<div className="flex flex-col items-center justify-center p-8 text-center text-white">
					<CameraIcon
						size={64}
						className="mx-auto mb-4 animate-pulse opacity-50"
					/>
					<p className="text-sm text-gray-400">Initializing camera...</p>
				</div>
			);
		}

		if (error) {
			return (
				<div className="flex flex-col items-center justify-center p-4 pb-32 text-center text-white sm:p-8 sm:pb-24">
					<CameraIcon size={64} className="mx-auto mb-4 opacity-50" />
					<p className="mb-4 text-base font-semibold sm:text-lg">{error}</p>
					{error.includes("denied") || error.includes("Permission") ? (
						<div className="flex flex-col items-center space-y-3">
							<p className="text-xs text-gray-300 sm:text-sm">
								To enable camera access:
							</p>
							<ul className="mx-auto max-w-md space-y-2 text-center text-xs text-gray-400 sm:text-sm">
								<li className="flex items-start justify-center gap-2">
									<span className="mt-1">1.</span>
									<span>
										Look for the camera icon in your browser&apos;s address bar
									</span>
								</li>
								<li className="flex items-start justify-center gap-2">
									<span className="mt-1">2.</span>
									<span>
										Click it and select &quot;Allow&quot; for camera access
									</span>
								</li>
								<li className="flex items-start justify-center gap-2">
									<span className="mt-1">3.</span>
									<span>Refresh this page and try again</span>
								</li>
							</ul>
							<Button
								onClick={() => {
									setError(null);
									startCamera();
								}}
								className="mt-4 bg-white text-black hover:bg-gray-200"
							>
								Try Again
							</Button>
						</div>
					) : error.includes("connection lost") ||
					  error.includes("Connection lost") ? (
						<div className="flex flex-col items-center space-y-3">
							<p className="text-xs text-gray-300 sm:text-sm">
								{error.includes("Camera")
									? "Please reconnect your camera and try again."
									: "Please reconnect your microphone and try again."}
							</p>
							<Button
								onClick={() => {
									setError(null);
									if (mode === "audio") {
										setHasPermission(true);
									} else {
										startCamera();
									}
								}}
								className="mt-4 bg-white text-black hover:bg-gray-200"
							>
								Try Again
							</Button>
						</div>
					) : (
						<div className="flex flex-col items-center space-y-3">
							<p className="text-xs text-gray-400 sm:text-sm">
								Please check your camera connection and browser settings
							</p>
							<Button
								onClick={() => {
									setError(null);
									if (mode !== "audio") {
										startCamera();
									}
								}}
								className="mt-4 bg-white text-black hover:bg-gray-200"
							>
								Try Again
							</Button>
						</div>
					)}
				</div>
			);
		}

		if (showCrop && imageToCrop) {
			return (
				<ImageCropTool
					imageToCrop={imageToCrop}
					crop={crop}
					onCropChange={onCropChange}
					onCropComplete={onCropComplete}
					onApplyCrop={applyCrop}
					imgRef={imgRef}
					onCancel={() => {
						cancelCrop();
						startCamera();
					}}
					onImageLoaded={onImageLoaded}
				/>
			);
		}

		if (hasCapturedMedia) {return null;}

		if (autoTriggerUpload && !imageToCrop && !capturedVideo && !capturedAudio) {
			return (
				<div className="flex h-full w-full flex-col items-center justify-center gap-6 bg-gradient-to-b from-gray-900 via-black to-gray-900 p-8">
					<div className="relative">
						<div className="flex h-64 w-64 items-center justify-center rounded-2xl border-4 border-dashed border-gray-600 bg-gray-800/50 backdrop-blur-sm">
							<div className="text-center">
								<img
									src={`${IMAGE_PATH}/newsfeed/upload.png`}
									alt="Upload"
									className="mx-auto mb-4 h-16 w-auto opacity-60"
								/>
								<p className="text-lg font-semibold text-gray-300">
									{mode === "photo" ? "Image Preview" : "Video Preview"}
								</p>
								<p className="mt-2 text-sm text-gray-500">
									Your {mode === "photo" ? "image" : "video"} will appear here
								</p>
							</div>
						</div>
					</div>
					<div className="text-center">
						<p className="text-xl font-bold text-white">
							Select a file to upload
						</p>
						<p className="mt-2 text-sm text-gray-400">
							Click the upload button below to choose a{" "}
							{mode === "photo" ? "photo" : "video"}
						</p>
					</div>
				</div>
			);
		}

		if (mode === "audio") {
			return (
				<div className="flex h-full w-full flex-col items-center justify-center gap-6 bg-gradient-to-b from-black via-gray-900 to-black p-8 pb-32 sm:gap-8 sm:pb-8">
					<div className="text-center">
						<p className="text-2xl font-bold text-white">
							{isAudioRecording ? "Recording..." : "Ready to Record"}
						</p>
						{isAudioRecording ? <p className="mt-2 font-mono text-xl text-yellow-400">
								{formatDuration(recordingDuration)}
							</p> : null}
					</div>
					<div className="w-full max-w-md">
						<Waveform
							audioLevels={audioLevels}
							isRecording={isAudioRecording}
						/>
					</div>
					<p className="max-w-sm text-center text-sm text-gray-400">
						{isAudioRecording
							? "Tap the button below to stop recording"
							: "Tap the microphone button below to start recording"}
					</p>
				</div>
			);
		}

		// Live camera view (photo / video)
		return (
			<div className="relative flex h-full w-full items-center justify-center bg-black">
				<div
					ref={cameraContainerRef}
					className="relative h-full w-full md:mx-auto md:w-[calc(100vh*9/16)] md:max-w-[calc(100vw-2rem)]"
				>
					<CameraStream
						videoRef={videoRef}
						stream={stream}
						facingMode={facingMode}
						hasPermission={hasPermission}
						isInitializing={isInitializing}
						onUserInteraction={handleUserInteraction}
					/>
					{mode === "photo" && !isRecording && (
						<PhotoCaptureMode
							videoRef={videoRef}
							canvasRef={canvasRef}
							facingMode={facingMode}
							onCapture={handlePhotoCapture}
							onUserInteraction={handleUserInteraction}
							isInitializing={isInitializing}
							hasPermission={hasPermission}
						/>
					)}
					{mode === "video" && (
						<VideoRecordingMode
							isRecording={isRecording}
							recordingDuration={recordingDuration}
							formatDuration={formatDuration}
							onStartRecording={startRecording}
							onStopRecording={stopRecording}
							onUserInteraction={handleUserInteraction}
							isInitializing={isInitializing}
							hasPermission={hasPermission}
							isMuted={isMuted}
							onToggleMute={() => setIsMuted(!isMuted)}
						/>
					)}
				</div>
			</div>
		);
	};

	const renderBottomControls = () => {
		if (autoTriggerUpload && !imageToCrop && !capturedVideo && !capturedAudio) {
			return (
				<div className="flex flex-col items-center gap-4 pb-0">
					<div className="flex items-center justify-center pb-4">
						<Button
							onClick={() => fileInputRef.current?.click()}
							size="lg"
							className="bg-gradient-to-r from-green-500 to-emerald-500 px-8 py-6 text-lg font-bold text-white shadow-lg hover:from-green-600 hover:to-emerald-600"
						>
							<img
								src={`${IMAGE_PATH}/newsfeed/upload.png`}
								alt="Upload"
								className="mr-3 h-6 w-auto brightness-0 invert"
							/>
							Choose File
						</Button>
					</div>
				</div>
			);
		}

		if (hasCapturedMedia) {return null;}

		if (mode === "audio") {
			return (
				<div className="flex flex-col items-center gap-4 pb-0">
					<div className="flex items-center justify-center pb-4">
						<Button
							onClick={
								isAudioRecording ? stopAudioRecording : startAudioRecording
							}
							disabled={!hasPermission}
							size="icon"
							className={`h-16 w-16 rounded-full border-4 border-white shadow-xl transition-all sm:h-20 sm:w-20 ${
								isAudioRecording
									? "bg-red-500 hover:bg-red-600"
									: "bg-white hover:bg-gray-200"
							}`}
						>
							{isAudioRecording ? (
								<>
									<div className="h-6 w-6 rounded-sm bg-white sm:hidden" />
									<div className="hidden h-8 w-8 rounded-sm bg-white sm:block" />
								</>
							) : (
								<>
									<Mic size={32} className="text-black sm:hidden" />
									<Mic size={40} className="hidden text-black sm:block" />
								</>
							)}
						</Button>
					</div>
					<CameraControls
						mode={mode}
						isRecording={isRecording}
						isAudioRecording={isAudioRecording}
						onSetMode={setMode}
						onUploadClick={() => fileInputRef.current?.click()}
						onStopRecording={stopRecording}
					/>
				</div>
			);
		}

		if (!hideBottomBar) {
			return (
				<CameraControls
					mode={mode}
					isRecording={isRecording}
					isAudioRecording={isAudioRecording}
					onSetMode={setMode}
					onUploadClick={() => fileInputRef.current?.click()}
					onStopRecording={stopRecording}
				/>
			);
		}

		return null;
	};

	const cameraContent = (
		<>
			{/* Backdrop */}
			<div
				className="fixed inset-0 z-[99998] bg-black/50"
				style={{ pointerEvents: "auto" }}
				onClick={(e) => e.stopPropagation()}
				onTouchStart={(e) => e.stopPropagation()}
				onTouchEnd={(e) => e.stopPropagation()}
				onMouseDown={(e) => e.stopPropagation()}
			/>

			<div
				className="fixed inset-0 z-[99999] flex flex-col items-center bg-black"
				style={{ pointerEvents: "auto" }}
				onClick={(e) => {
					e.stopPropagation();
					handleUserInteraction();
				}}
				onTouchStart={(e) => {
					e.stopPropagation();
					handleUserInteraction();
				}}
				onTouchEnd={(e) => e.stopPropagation()}
				onMouseDown={(e) => e.stopPropagation()}
			>
				<CameraHeader
					postType={postType}
					isContest={isContest}
					onClose={handleClose}
					hasMultipleCameras={hasMultipleCameras}
					capturedImage={capturedImage}
					capturedVideo={capturedVideo}
					capturedAudio={capturedAudio}
					mode={mode}
					facingMode={facingMode}
					onFlipCamera={switchCamera}
				/>

				{/* Hidden file inputs */}
				<input
					ref={fileInputRef}
					type="file"
					accept={
						autoTriggerUpload
							? "image/jpeg,image/png,image/gif,image/webp,video/mp4,video/webm,video/ogg,video/quicktime"
							: "image/jpeg,image/png,image/gif,image/webp"
					}
					onChange={(e) => {
						const f = e.target.files[0];
						if (f)
							{handleFileSelect(
								e,
								f.type.startsWith("video/") ? "video" : "image"
							);}
					}}
					className="hidden"
				/>
				<input
					ref={videoInputRef}
					type="file"
					accept="video/mp4,video/webm,video/ogg,video/quicktime"
					onChange={(e) => handleFileSelect(e, "video")}
					className="hidden"
				/>
				<input
					ref={audioInputRef}
					type="file"
					accept="audio/mpeg,audio/wav,audio/ogg,audio/webm,audio/mp4"
					onChange={(e) => handleFileSelect(e, "audio")}
					className="hidden"
				/>

				{hasCapturedMedia ? <MediaPreviewPanel
						capturedImage={capturedImage}
						capturedVideo={capturedVideo}
						capturedAudio={capturedAudio}
						imageToCrop={imageToCrop}
						isUploadedMedia={
							isUploadedMedia || isUploadedVideo || isUploadedAudio
						}
						mode={mode}
						confirmButtonLabel={confirmButtonLabel}
						onRetake={handleRetake}
						onUndo={handleUndo}
						onConfirm={handleConfirm}
					/> : null}

				<div className="relative flex w-full min-w-0 flex-1 items-center justify-center bg-black">
					{renderMainContent()}
				</div>

				<canvas ref={canvasRef} className="hidden" />

				<div className="absolute bottom-0 left-0 right-0 z-10 bg-gradient-to-t from-black/80 to-transparent">
					{renderBottomControls()}
				</div>
			</div>

			<FloatingSuggestions
				suggestions={floatingSuggestions}
				specs={floatingSpecs}
				hasUserInteracted={hasUserInteracted}
			/>
		</>
	);

	if (typeof document !== "undefined") {
		return createPortal(cameraContent, document.body);
	}
	return cameraContent;
};

export default Camera;
